import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { apiJson, ApiError } from '@/lib/api';
import { formatPhoneNumber, normalizeUsPhoneNumber } from '@/lib/phone';
import type {
  PreWhitelistedNumberAdminItem,
  PreWhitelistedNumberListResponse,
  SendPushNotificationResponse,
  SendTestResendEmailResponse,
} from '@/lib/types';

const LIST_QUERY_KEY = ['admin', 'pre-whitelisted-numbers'] as const;

function parseDetail(err: unknown): string {
  if (err instanceof ApiError && err.bodyText) {
    try {
      const j = JSON.parse(err.bodyText) as { detail?: string | string[] };
      if (typeof j.detail === 'string') return j.detail;
      if (Array.isArray(j.detail)) return j.detail.map(String).join('; ');
    } catch {
      /* ignore */
    }
    return err.bodyText;
  }
  return err instanceof Error ? err.message : 'Request failed';
}

/** Parse comma- or whitespace-separated user IDs; empty input means broadcast (null). */
function parseOptionalUserIds(raw: string): number[] | null {
  const parts = raw
    .split(/[\s,]+/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return null;
  const ids: number[] = [];
  for (const p of parts) {
    const n = Number(p);
    if (!Number.isInteger(n) || n <= 0) {
      throw new Error(`Invalid user id: "${p}"`);
    }
    ids.push(n);
  }
  return ids;
}

type ConfirmDialogState = {
  title: string;
  message: string;
  confirmLabel: string;
  destructive?: boolean;
  onConfirm: () => void;
};

function ConfirmDialog({
  open,
  state,
  onClose,
}: {
  open: boolean;
  state: ConfirmDialogState | null;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !state) return null;

  const confirmClass = state.destructive
    ? 'bg-red-600 text-white hover:bg-red-700'
    : 'bg-primary text-white hover:opacity-90';

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
      <div
        className='absolute inset-0 bg-black/40'
        aria-hidden
        onClick={onClose}
      />
      <div
        className='relative z-10 bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4 border border-gray-200'
        role='dialog'
        aria-modal='true'
        aria-labelledby='confirm-dialog-title'
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id='confirm-dialog-title'
          className='text-lg font-semibold text-gray-900'
        >
          {state.title}
        </h3>
        <p className='text-sm text-gray-600 whitespace-pre-wrap'>
          {state.message}
        </p>
        <div className='flex justify-end gap-2 pt-2'>
          <button
            type='button'
            onClick={onClose}
            className='px-4 py-2 text-sm font-semibold border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50'
          >
            Cancel
          </button>
          <button
            type='button'
            onClick={() => {
              state.onConfirm();
              onClose();
            }}
            className={`px-4 py-2 text-sm font-semibold rounded-md ${confirmClass}`}
          >
            {state.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminToolsPage() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [phoneDisplay, setPhoneDisplay] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(
    null
  );

  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [pushDeeplink, setPushDeeplink] = useState('');
  const [pushUserIdsRaw, setPushUserIdsRaw] = useState('');
  const [pushBanner, setPushBanner] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);

  const [resendTestEmail, setResendTestEmail] = useState('');
  const [resendBanner, setResendBanner] = useState<{
    ok: boolean;
    text: string;
    messageId?: string | null;
  } | null>(null);

  const listQuery = useQuery({
    queryKey: LIST_QUERY_KEY,
    queryFn: () =>
      apiJson<PreWhitelistedNumberListResponse>(
        getToken,
        '/v1/admin/pre-whitelisted-numbers'
      ),
  });

  const normalized = normalizeUsPhoneNumber(phoneDisplay);
  const canSubmit = normalized.length > 0;

  const addMutation = useMutation({
    mutationFn: () =>
      apiJson<PreWhitelistedNumberAdminItem>(
        getToken,
        '/v1/admin/pre-whitelisted-numbers',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone_number: normalized }),
        }
      ),
    onSuccess: async () => {
      setFormError(null);
      setPhoneDisplay('');
      await queryClient.invalidateQueries({ queryKey: LIST_QUERY_KEY });
    },
    onError: (e) => setFormError(parseDetail(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      apiJson<void>(getToken, `/v1/admin/pre-whitelisted-numbers/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: async () => {
      setDeleteError(null);
      await queryClient.invalidateQueries({ queryKey: LIST_QUERY_KEY });
    },
    onError: (e) => setDeleteError(parseDetail(e)),
  });

  const items = listQuery.data?.items ?? [];

  const pushMutation = useMutation({
    mutationFn: (userIds: number[] | null) => {
      const data =
        pushDeeplink.trim().length > 0
          ? { deeplink: pushDeeplink.trim() }
          : null;
      return apiJson<SendPushNotificationResponse>(
        getToken,
        '/v1/admin/send-push-notifications',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: pushTitle.trim(),
            body: pushBody.trim(),
            data,
            user_ids: userIds,
          }),
        }
      );
    },
    onSuccess: (data) => {
      setPushBanner({ ok: data.success, text: data.message });
      if (data.success) {
        setPushTitle('');
        setPushBody('');
        setPushDeeplink('');
        setPushUserIdsRaw('');
      }
    },
    onError: (e) => setPushBanner({ ok: false, text: parseDetail(e) }),
  });

  const canSendPush = pushTitle.trim().length > 0 && pushBody.trim().length > 0;

  const resendTestMutation = useMutation({
    mutationFn: () =>
      apiJson<SendTestResendEmailResponse>(
        getToken,
        '/v1/admin/send-test-resend-email',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: resendTestEmail.trim() }),
        }
      ),
    onSuccess: (data) => {
      setResendBanner({
        ok: data.success,
        text: data.message,
        messageId: data.resend_message_id,
      });
    },
    onError: (e) =>
      setResendBanner({ ok: false, text: parseDetail(e), messageId: null }),
  });

  const canSendResendTest = resendTestEmail.trim().length >= 3;

  const requestSendPush = () => {
    if (!canSendPush) return;
    setPushBanner(null);
    let userIds: number[] | null;
    try {
      userIds = parseOptionalUserIds(pushUserIdsRaw);
    } catch (e) {
      setPushBanner({
        ok: false,
        text: e instanceof Error ? e.message : 'Invalid user IDs',
      });
      return;
    }

    const title = pushTitle.trim();
    const body = pushBody.trim();
    const deeplink = pushDeeplink.trim();
    const preview =
      `Title: ${title}\n\nBody:\n${body}` +
      (deeplink ? `\n\nDeeplink: ${deeplink}` : '') +
      (userIds === null
        ? '\n\nAudience: ALL users with a push token.'
        : `\n\nAudience: ${userIds.length} user(s) with IDs: ${userIds.join(
            ', '
          )}.`);

    setConfirmDialog({
      title: 'Send push notification?',
      message:
        userIds === null
          ? `${preview}\n\nThis cannot be undone. Are you sure?`
          : `${preview}\n\nSend to these users only?`,
      confirmLabel: 'Send push',
      destructive: userIds === null,
      onConfirm: () => pushMutation.mutate(userIds),
    });
  };

  return (
    <div className='space-y-6'>
      <h1 className='text-2xl font-semibold text-gray-900'>Tools</h1>

      <section className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-4'>
        <h2 className='text-lg font-semibold text-gray-900'>
          Pre-whitelisted numbers
        </h2>
        <p className='text-sm text-gray-600'>
          Numbers on this list receive subscription override when a matching
          user signs up. US numbers only (+1).
        </p>

        <form
          className='flex flex-wrap gap-3 items-end'
          onSubmit={(e) => {
            e.preventDefault();
            if (!canSubmit) return;
            setFormError(null);
            setConfirmDialog({
              title: 'Add pre-whitelisted number?',
              message: `Add ${normalized} to the pre-whitelist? They will receive subscription override when they sign up with this phone number.`,
              confirmLabel: 'Add number',
              onConfirm: () => addMutation.mutate(),
            });
          }}
        >
          <div className='flex-1 min-w-[220px]'>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>
              Add phone number
            </label>
            <div className='flex items-center gap-2'>
              <span className='text-sm text-gray-500 select-none'>+1</span>
              <input
                type='tel'
                value={phoneDisplay}
                onChange={(e) => {
                  setPhoneDisplay(formatPhoneNumber(e.target.value));
                  setFormError(null);
                }}
                placeholder='(555) 123-4567'
                className='flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md text-sm'
              />
            </div>
          </div>
          <button
            type='submit'
            disabled={!canSubmit || addMutation.isPending}
            className='px-4 py-2 rounded-md text-sm font-semibold text-white bg-primary hover:opacity-90 disabled:opacity-50'
          >
            {addMutation.isPending ? 'Adding...' : 'Add'}
          </button>
        </form>
        {formError && <p className='text-sm text-red-600'>{formError}</p>}
        {deleteError && <p className='text-sm text-red-600'>{deleteError}</p>}

        <div className='border border-gray-200 rounded-md overflow-hidden'>
          <table className='w-full text-sm'>
            <thead className='bg-gray-50 text-xs uppercase text-gray-500'>
              <tr>
                <th scope='col' className='px-4 py-3 text-left'>
                  Phone
                </th>
                <th scope='col' className='px-4 py-3 text-left'>
                  Added
                </th>
                <th scope='col' className='px-4 py-3 text-left'>
                  Status
                </th>
                <th scope='col' className='px-4 py-3 text-right'>
                  User ID
                </th>
                <th scope='col' className='px-4 py-3 text-right'>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-100'>
              {listQuery.isLoading && (
                <tr>
                  <td
                    colSpan={5}
                    className='px-4 py-8 text-center text-gray-400'
                  >
                    Loading...
                  </td>
                </tr>
              )}
              {listQuery.isError && (
                <tr>
                  <td
                    colSpan={5}
                    className='px-4 py-8 text-center text-red-600'
                  >
                    {parseDetail(listQuery.error)}
                  </td>
                </tr>
              )}
              {!listQuery.isLoading &&
                !listQuery.isError &&
                items.map((row) => (
                  <tr key={row.id} className='hover:bg-gray-50'>
                    <td className='px-4 py-2 font-mono tabular-nums'>
                      {row.phone_number}
                    </td>
                    <td className='px-4 py-2 text-gray-600'>
                      {new Date(row.created_at).toLocaleString()}
                    </td>
                    <td className='px-4 py-2'>
                      {row.applied ? (
                        <span className='inline-flex px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold'>
                          Applied
                        </span>
                      ) : (
                        <span className='inline-flex px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold'>
                          Pending
                        </span>
                      )}
                    </td>
                    <td className='px-4 py-2 text-right tabular-nums text-gray-700'>
                      {row.applied_to_user_id ?? '-'}
                    </td>
                    <td className='px-4 py-2 text-right'>
                      <button
                        type='button'
                        disabled={deleteMutation.isPending}
                        onClick={() => {
                          const message = row.applied
                            ? `Remove ${row.phone_number}? It was already applied to user ${row.applied_to_user_id}.`
                            : `Remove ${row.phone_number} from the pre-whitelist?`;
                          setConfirmDialog({
                            title: 'Remove pre-whitelisted number?',
                            message,
                            confirmLabel: 'Remove',
                            destructive: true,
                            onConfirm: () => deleteMutation.mutate(row.id),
                          });
                        }}
                        className='inline-flex px-2 py-1 rounded-md border border-red-200 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50'
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              {!listQuery.isLoading &&
                !listQuery.isError &&
                items.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className='px-4 py-8 text-center text-gray-400'
                    >
                      No pre-whitelisted numbers.
                    </td>
                  </tr>
                )}
            </tbody>
          </table>
        </div>
      </section>

      <section className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-4'>
        <h2 className='text-lg font-semibold text-gray-900'>
          Push notifications
        </h2>
        <p className='text-sm text-gray-600'>
          Uses{' '}
          <code className='text-xs bg-gray-100 px-1 py-0.5 rounded'>
            POST /v1/admin/send-push-notifications
          </code>
          . Leave user IDs empty to target everyone with a registered push token
          (requires confirmation).
        </p>
        {pushBanner ? (
          <div
            className={`rounded-lg border px-4 py-3 text-sm ${
              pushBanner.ok
                ? 'bg-green-50 border-green-200 text-green-900'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            {pushBanner.text}
          </div>
        ) : null}
        <div className='space-y-3'>
          <input
            value={pushTitle}
            onChange={(e) => {
              setPushTitle(e.target.value);
              setPushBanner(null);
            }}
            placeholder='Title'
            className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
          />
          <textarea
            value={pushBody}
            onChange={(e) => {
              setPushBody(e.target.value);
              setPushBanner(null);
            }}
            placeholder='Body'
            rows={3}
            className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
          />
          <input
            value={pushDeeplink}
            onChange={(e) => {
              setPushDeeplink(e.target.value);
              setPushBanner(null);
            }}
            placeholder='Deeplink / data.deeplink (optional)'
            className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
          />
          <div>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>
              User IDs (optional)
            </label>
            <input
              value={pushUserIdsRaw}
              onChange={(e) => {
                setPushUserIdsRaw(e.target.value);
                setPushBanner(null);
              }}
              placeholder='e.g. 12, 34 - leave empty for all users with a token'
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
            />
          </div>
          <button
            type='button'
            disabled={!canSendPush || pushMutation.isPending}
            onClick={requestSendPush}
            className='px-4 py-2 text-sm font-semibold bg-primary text-white rounded-md disabled:opacity-50'
          >
            {pushMutation.isPending ? 'Sending...' : 'Send push'}
          </button>
        </div>
      </section>

      <section className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-4'>
        <h2 className='text-lg font-semibold text-gray-900'>
          Resend test email
        </h2>
        <p className='text-sm text-gray-600'>
          Sends a short HTML test via{' '}
          <code className='text-xs bg-gray-100 px-1 py-0.5 rounded'>
            POST /v1/admin/send-test-resend-email
          </code>
          . Safe to send to yourself repeatedly.
        </p>
        {resendBanner ? (
          <div
            className={`rounded-lg border px-4 py-3 text-sm ${
              resendBanner.ok
                ? 'bg-green-50 border-green-200 text-green-900'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            <p>{resendBanner.text}</p>
            {resendBanner.ok && resendBanner.messageId ? (
              <p className='mt-2 font-mono text-xs opacity-90 break-all'>
                resend_message_id: {resendBanner.messageId}
              </p>
            ) : null}
          </div>
        ) : null}
        <div className='flex flex-wrap gap-3 items-end'>
          <div className='flex-1 min-w-[220px]'>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>
              Recipient email
            </label>
            <input
              type='email'
              autoComplete='email'
              value={resendTestEmail}
              onChange={(e) => {
                setResendTestEmail(e.target.value);
                setResendBanner(null);
              }}
              placeholder='you@example.com'
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
            />
          </div>
          <button
            type='button'
            disabled={!canSendResendTest || resendTestMutation.isPending}
            onClick={() => {
              setResendBanner(null);
              resendTestMutation.mutate();
            }}
            className='px-4 py-2 text-sm font-semibold bg-primary text-white rounded-md disabled:opacity-50'
          >
            {resendTestMutation.isPending ? 'Sending...' : 'Send Resend test'}
          </button>
        </div>
      </section>

      <ConfirmDialog
        open={confirmDialog !== null}
        state={confirmDialog}
        onClose={() => setConfirmDialog(null)}
      />
    </div>
  );
}

export const Route = createFileRoute('/admin/tools')({
  component: AdminToolsPage,
});
